import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  afterNextRender,
  PLATFORM_ID
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputSwitchModule } from 'primeng/inputswitch';
import { filter, firstValueFrom } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import { SharedUtilsModule } from '@usheru-hff/shared/utils';
import { CookieSettings, CookieSettingsService, CookiesSettingsType } from '@usheru-hff/shared/utils-cookies';
import { TooltipComponent } from '@usheru-hff/ui/tooltip';

/**
 * //TODO docs favio
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - use env values in their own method and subscribe to env changes
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    SharedUtilsModule,
    DialogModule,
    InputSwitchModule,
    FontAwesomeModule,
    TooltipComponent
  ]
})
export class CookieConsentComponent implements OnInit, AfterViewInit {
  displayCookieDialog = false;
  showCookieBanner = false;
  allowLoginChange = true;
  customCookiesMsg: string;
  cookieSettings: CookieSettings;
  private hasTrackedBannerView = false;

  tempCookieSettings: CookieSettings;

  faQuestionCircle = faQuestionCircle;

  joinedTargetingPlatforms = '';
  hasTargetingPlatforms = false;

  readonly cookiePolicyUrl = this.env.components?.cookieConsent?.policyUrl;
  readonly consentProviderName = this.env.components?.cookieConsent?.providerName;

  @ViewChild('popupmsg')
  popupmsg: ElementRef;

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    @Inject(PLATFORM_ID) private platform,
    private cookieSettingsService: CookieSettingsService,
    private channelService: ChannelService,
    private router: Router,
    private cd: ChangeDetectorRef,
    @Inject(TRACKING_SERVICE) @Inject(TRACKING_SERVICE) private trackingService: TrackingService
  ) {
    afterNextRender(() => {
      this.setInitialCookieSettings();
    });
  }

  ngOnInit(): void {
    this.cookieSettingsService.$openCookiesConsentDialog.subscribe(settings => {
      this.showCookieCustomizationDialog(settings);
      this.setTargetingPlatforms();
    });
  }

  ngAfterViewInit(): void {
    // replace popupmsg link with the angular redirection
    const links = (this.popupmsg?.nativeElement as HTMLElement)?.querySelectorAll('a');
    links?.forEach(link => {
      const href = link.getAttribute('href');
      link.addEventListener('click', e => {
        if (href.indexOf('/') == 0) {
          e.preventDefault();
          this.router.navigateByUrl(href);
        }
      });
    });
  }

  setInitialCookieSettings(): void {
    this.cookieSettings = this.cookieSettingsService.getCookieSettings();
    this.showCookieBanner = !!this.cookieSettings && !this.cookieSettings.accepted;
    this.trackBannerViewIfVisible();

    if (!this.env.oAuthFacebook && !this.env.oAuthGoogle) {
      this.allowLoginChange = false;
    }
  }

  private setTargetingPlatforms(): void {
    const targetingPlatforms: string[] = [];
    const channel = this.channelService.channel;
    if (channel?.googleAnalytics4 || channel?.googleTag || channel?.googleAdSense) {
      targetingPlatforms.push('Google');
    }
    if (channel.facebookPixel) {
      targetingPlatforms.push('Facebook');
    }
    if (channel.twitter) {
      targetingPlatforms.push('Twitter');
    }
    if (channel.snapchat) {
      targetingPlatforms.push('SnapChat');
    }

    this.joinedTargetingPlatforms = targetingPlatforms.join(', ');
    this.hasTargetingPlatforms = targetingPlatforms.length > 0;
  }

  private trackBannerViewIfVisible(): void {
    if (!this.isBrowser || !this.showCookieBanner || this.hasTrackedBannerView) {
      return;
    }

    this.trackingService.trackEvent({
      action: 'CookieBannerViewed',
      properties: {
        category: 'Cookies'
      }
    });
    this.hasTrackedBannerView = true;
  }

  showCookieCustomizationDialog(cookiesSettingsTypes?: CookiesSettingsType[]) {
    this.tempCookieSettings = JSON.parse(JSON.stringify(this.cookieSettings));
    this.resetValues();
    if (cookiesSettingsTypes) {
      cookiesSettingsTypes.forEach(type => {
        switch (type) {
          case 'security_storage':
            this.cookieSettings.security_storage.enabled = true;
            this.cookieSettings.security_storage.highlighted = true;
            this.customCookiesMsg = $localize`:@@custom-cookies-msg.security_storage_needed:Security cookies are needed for this service`;
            break;
          case 'analytics_storage':
            this.cookieSettings.analytics_storage.enabled = true;
            this.cookieSettings.analytics_storage.highlighted = true;
            this.customCookiesMsg = $localize`:@@custom-cookies-msg.analytics_storage_needed:Analytics cookies are needed for this service.`;
            break;
          case 'ad_storage':
            this.cookieSettings.ad_storage.enabled = true;
            this.cookieSettings.ad_storage.highlighted = true;
            this.customCookiesMsg = $localize`:@@custom-cookies-msg.ad_storage_needed:Targeting cookies are needed for this service.`;
            break;
          case 'functionality_storage':
            this.cookieSettings.functionality_storage.enabled = true;
            this.cookieSettings.functionality_storage.highlighted = true;
            this.customCookiesMsg = $localize`:@@custom-cookies-msg.functionality_storage_needed:Functionality cookies are needed for this service.`;
            break;
        }
      });
    }
    this.displayCookieDialog = true;
    this.trackingService.trackEvent({
      action: 'CookieMoreOptions',
      properties: {
        category: 'Cookies'
      }
    });
    this.cd.detectChanges();
  }

  acceptChoices(): void {
    this.tempCookieSettings = null;
    this.displayCookieDialog = false;
    this.cookieSettings.accepted = true;
    this.showCookieBanner = false;
    this.cookieSettingsService.saveCookieSettings(this.cookieSettings);
    this.cookieSettingsService.notifyCookieConsentDialogClosed();
    this.cd.detectChanges();
  }

  acceptAll(): void {
    this.displayCookieDialog = false;
    this.cookieSettings.accepted = true;
    this.showCookieBanner = false;
    this.cookieSettings.analytics_storage.enabled = true;
    this.cookieSettings.security_storage.enabled = true;
    this.cookieSettings.functionality_storage.enabled = true;
    this.cookieSettings.ad_storage.enabled = true;
    this.cookieSettings.social_login.enabled = true;

    this.cookieSettingsService.saveCookieSettings(this.cookieSettings);
    this.cookieSettingsService.notifyCookieConsentDialogClosed();
    this.trackingService.trackEvent({
      action: 'CookieAcceptAll',
      properties: {
        category: 'Cookies'
      }
    });
    this.cd.detectChanges();
  }

  rejectAll(): void {
    this.tempCookieSettings = null;
    this.displayCookieDialog = false;
    this.cookieSettings.accepted = true;
    this.showCookieBanner = false;
    this.cookieSettings.security_storage.enabled = false;
    this.cookieSettings.analytics_storage.enabled = false;
    this.cookieSettings.functionality_storage.enabled = false;
    this.cookieSettings.ad_storage.enabled = false;
    this.cookieSettings.social_login.enabled = false;
    this.cookieSettingsService.saveCookieSettings(this.cookieSettings);
    this.cookieSettingsService.notifyCookieConsentDialogClosed();
    this.trackingService.trackEvent({
      action: 'CookiesRejectAll',
      properties: {
        category: 'Cookies'
      }
    });
    this.cd.detectChanges();
  }

  cancelSettings(): void {
    if (this.tempCookieSettings) {
      this.cookieSettings = JSON.parse(JSON.stringify(this.tempCookieSettings));
      this.cookieSettingsService.setCookiesSettings(this.cookieSettings);
    }
    this.displayCookieDialog = false;
    this.cookieSettingsService.notifyCookieConsentDialogClosed();
    this.cd.detectChanges();
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platform);
  }

  resetValues(): void {
    this.customCookiesMsg = '';
    this.cookieSettings.analytics_storage.highlighted = false;
    this.cookieSettings.ad_storage.highlighted = false;
    this.cookieSettings.functionality_storage.highlighted = false;
    this.cookieSettings.security_storage.highlighted = false;
    this.cookieSettings.social_login.highlighted = false;
    this.cd.detectChanges();
  }
}
